'use client'

import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const ZOOM_MIN = 0.5
const ZOOM_MAX = 3
const ZOOM_STEP = 0.25
const MAX_PX = 2200 // cap a page's rendered bitmap width (memory) — sets the crispness ceiling
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

// PDF viewer (PDF.js → canvas; works on iOS Safari where inline iframe PDFs don't). Each page is
// rendered ONCE at high resolution; zooming just resizes the displayed bitmap (CSS width) — no
// per-zoom re-render — so a pinch can zoom continuously and smoothly. Pan = native scroll; we run
// our own pan/pinch from touch events (reliable on multi-touch) and block the browser's page-zoom.
export function PdfModal({
  url,
  label = 'View bylaws & rules',
  title = 'Bylaws & Rules',
}: {
  url: string
  label?: string
  title?: string
}) {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<'loading' | 'done' | 'error'>('loading')
  const [zoom, setZoom] = useState(1)
  const canvasHost = useRef<HTMLDivElement>(null)
  const docRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef(1)
  const fitWRef = useRef(0) // fit-to-width (zoom = 1) display width
  zoomRef.current = zoom
  const pan = useRef({ active: false, x: 0, y: 0, sl: 0, st: 0 })
  const pinch = useRef({ active: false, startDist: 0, startZoom: 1 })

  // Resize the displayed bitmaps (no re-render) and keep the anchor point fixed under the fingers /
  // viewport centre, so zooming doesn't shoot the page off screen.
  const applyZoom = (next: number, anchorX?: number, anchorY?: number) => {
    const el = docRef.current
    const host = canvasHost.current
    if (!el || !host || !fitWRef.current) return
    const target = clamp(+next.toFixed(3), ZOOM_MIN, ZOOM_MAX)
    const prev = zoomRef.current
    if (Math.abs(target - prev) < 0.0005) return
    const ax = anchorX ?? el.clientWidth / 2
    const ay = anchorY ?? el.clientHeight / 2
    const cx = el.scrollLeft + ax
    const cy = el.scrollTop + ay
    const f = target / prev
    host.querySelectorAll('canvas').forEach((c) => {
      ;(c as HTMLCanvasElement).style.width = `${fitWRef.current * target}px`
    })
    el.scrollLeft = cx * f - ax
    el.scrollTop = cy * f - ay
    zoomRef.current = target
    setZoom(target)
  }

  const openViewer = () => {
    zoomRef.current = 1
    setZoom(1)
    setOpen(true)
  }
  const zoomOut = () => applyZoom(zoomRef.current - ZOOM_STEP)
  const zoomIn = () => applyZoom(zoomRef.current + ZOOM_STEP)
  const resetZoom = () => applyZoom(1)

  // ── Mouse drag-pan (desktop) ──
  const onPointerDown = (e: React.PointerEvent) => {
    const el = docRef.current
    if (!el || e.pointerType !== 'mouse') return
    el.setPointerCapture?.(e.pointerId)
    pan.current = { active: true, x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop }
    el.classList.add('is-panning')
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const el = docRef.current
    if (!el || e.pointerType !== 'mouse' || !pan.current.active) return
    el.scrollLeft = pan.current.sl - (e.clientX - pan.current.x)
    el.scrollTop = pan.current.st - (e.clientY - pan.current.y)
  }
  const onPointerUp = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return
    pan.current.active = false
    docRef.current?.classList.remove('is-panning')
  }

  // ── Touch pan (1 finger) + pinch (2 fingers) ──
  const tDist = (t: React.TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY)
  const onTouchStart = (e: React.TouchEvent) => {
    const el = docRef.current
    if (!el) return
    if (e.touches.length >= 2) {
      pan.current.active = false
      pinch.current = { active: true, startDist: tDist(e.touches), startZoom: zoomRef.current }
    } else if (e.touches.length === 1) {
      pan.current = { active: true, x: e.touches[0].clientX, y: e.touches[0].clientY, sl: el.scrollLeft, st: el.scrollTop }
    }
  }
  const onTouchMove = (e: React.TouchEvent) => {
    const el = docRef.current
    if (!el) return
    if (pinch.current.active && e.touches.length >= 2) {
      const r = el.getBoundingClientRect()
      const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left
      const my = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top
      applyZoom(pinch.current.startZoom * (tDist(e.touches) / pinch.current.startDist), mx, my)
    } else if (pan.current.active && e.touches.length === 1) {
      el.scrollLeft = pan.current.sl - (e.touches[0].clientX - pan.current.x)
      el.scrollTop = pan.current.st - (e.touches[0].clientY - pan.current.y)
    }
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length === 0) {
      pinch.current.active = false
      pan.current.active = false
    }
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
      else if (e.key === '+' || e.key === '=') zoomIn()
      else if (e.key === '-') zoomOut()
      else if (e.key === '0') resetZoom()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    // Block the browser's own page pinch-zoom while the viewer is open (so an edge pinch can't
    // zoom/trap the page). Our pinch resizes the high-res bitmap instead.
    const stopMultiTouch = (ev: TouchEvent) => {
      if (ev.touches.length >= 2) ev.preventDefault()
    }
    const stopGesture = (ev: Event) => ev.preventDefault()
    document.addEventListener('touchmove', stopMultiTouch, { passive: false })
    document.addEventListener('gesturestart', stopGesture as EventListener)
    document.addEventListener('gesturechange', stopGesture as EventListener)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
      document.removeEventListener('touchmove', stopMultiTouch)
      document.removeEventListener('gesturestart', stopGesture as EventListener)
      document.removeEventListener('gesturechange', stopGesture as EventListener)
    }
  }, [open])

  // Render every page ONCE at high resolution. Zoom afterwards is pure bitmap resize (applyZoom).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setState('loading')
    ;(async () => {
      try {
        const pdfjs = await import('pdfjs-dist')
        pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'
        const host = canvasHost.current
        const docEl = host?.parentElement
        if (!host || !docEl) return
        const fitW = Math.min(docEl.clientWidth - 32, 900)
        fitWRef.current = fitW
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        const doc = await pdfjs.getDocument(url).promise
        const frag = document.createDocumentFragment()
        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return
          const page = await doc.getPage(n)
          const base = page.getViewport({ scale: 1 })
          // Oversample so CSS-resizing up to ~ZOOM_MAX stays crisp, capped for memory.
          const intrinsicW = Math.min(fitW * ZOOM_MAX * dpr, MAX_PX)
          const viewport = page.getViewport({ scale: intrinsicW / base.width })
          const canvas = document.createElement('canvas')
          canvas.className = 'pdf-page'
          canvas.width = viewport.width
          canvas.height = viewport.height
          canvas.style.width = `${fitW * zoomRef.current}px`
          canvas.style.height = 'auto'
          frag.appendChild(canvas)
          const ctx = canvas.getContext('2d')
          if (ctx) await page.render({ canvasContext: ctx, viewport }).promise
        }
        if (cancelled) return
        host.replaceChildren(frag)
        setState('done')
      } catch (e) {
        console.error('PDF render failed', e)
        if (!cancelled) setState('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, url])

  return (
    <>
      <button className="btn btn-primary" onClick={openViewer}>
        {label}
      </button>
      {open
        ? createPortal(
            <div
              className="pdf-modal"
              role="dialog"
              aria-modal="true"
              aria-label={title}
              onClick={() => setOpen(false)}
            >
              <div className="pdf-modal__panel" onClick={(e) => e.stopPropagation()}>
                <div className="pdf-modal__bar">
                  <strong>{title}</strong>
                  <span className="pdf-zoom" role="group" aria-label="Zoom">
                    <button onClick={zoomOut} disabled={zoom <= ZOOM_MIN} aria-label="Zoom out">
                      −
                    </button>
                    <button
                      className="pdf-zoom__pct"
                      onClick={resetZoom}
                      title="Reset to 100%"
                      aria-label="Reset zoom to 100%"
                    >
                      {Math.round(zoom * 100)}%
                    </button>
                    <button onClick={zoomIn} disabled={zoom >= ZOOM_MAX} aria-label="Zoom in">
                      +
                    </button>
                  </span>
                  <span className="pdf-modal__actions">
                    <a className="btn btn-outline" href={url} target="_blank" rel="noopener noreferrer">
                      Open
                    </a>
                    <a className="btn btn-outline" href={url} download>
                      Download
                    </a>
                    <button className="btn btn-outline" onClick={() => setOpen(false)}>
                      Close
                    </button>
                  </span>
                </div>
                <div
                  className="pdf-modal__doc"
                  ref={docRef}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerLeave={onPointerUp}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  onTouchCancel={onTouchEnd}
                >
                  {state === 'loading' ? <p className="pdf-modal__msg">Loading…</p> : null}
                  {state === 'error' ? (
                    <p className="pdf-modal__msg">
                      Couldn&apos;t display the PDF inline — use Open or Download above.
                    </p>
                  ) : null}
                  <div className="pdf-modal__canvases" ref={canvasHost} />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
